import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Globe, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import privacyEsUrl from '../text/text.txt';
import privacyEnUrl from '../text/text_en.txt';

const parsePolicyText = (rawText) => {
  const normalized = (rawText || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalized.split('\n');

  const elements = [];
  let listItems = [];

  const flushList = () => {
    if (!listItems.length) return;
    const items = listItems;
    listItems = [];
    elements.push(
      <ul key={`ul-${elements.length}`} className="list-disc pl-6 space-y-2 text-gray-700">
        {items.map((item) => (
          <li key={item} className="leading-relaxed">
            {item}
          </li>
        ))}
      </ul>
    );
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      return;
    }

    if (trimmed.startsWith('## ')) {
      flushList();
      const heading = trimmed.slice(3).trim();
      elements.push(
        <h2 key={`h2-${index}`} className="text-xl md:text-2xl font-bold text-[#141414] mt-8">
          {heading}
        </h2>
      );
      return;
    }

    if (trimmed.startsWith('- ')) {
      listItems.push(trimmed.slice(2).trim());
      return;
    }

    flushList();

    if (index === 0) {
      elements.push(
        <h1 key="h1" className="text-3xl md:text-4xl font-extrabold text-[#141414] tracking-tight">
          {trimmed}
        </h1>
      );
      return;
    }

    if (index === 1) {
      elements.push(
        <div key="meta" className="mt-3 text-sm text-gray-500 font-medium">
          {trimmed}
        </div>
      );
      return;
    }

    elements.push(
      <p key={`p-${index}`} className="text-gray-700 leading-relaxed mt-4">
        {line}
      </p>
    );
  });

  flushList();
  return elements;
};

const NextshotPrivacyPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [policyEs, setPolicyEs] = useState('');
  const [policyEn, setPolicyEn] = useState('');

  useEffect(() => {
    let isMounted = true;

    Promise.all([
      fetch(privacyEsUrl).then((res) => res.text()),
      fetch(privacyEnUrl).then((res) => res.text())
    ])
      .then(([esText, enText]) => {
        if (!isMounted) return;
        setPolicyEs(esText);
        setPolicyEn(enText);
      })
      .catch(() => {
        if (!isMounted) return;
        setPolicyEs('');
        setPolicyEn('');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const language = (i18n.resolvedLanguage || i18n.language || 'es').toLowerCase();
  const activeLang = language.startsWith('pt') ? 'pt' : language.startsWith('en') ? 'en' : 'es';
  const policyContent = activeLang === 'es' ? policyEs : policyEn;
  const parsedContent = useMemo(() => parsePolicyText(policyContent), [policyContent]);

  return (
    <div className="relative min-h-screen bg-transparent font-inter">
      <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src="https://imgur.com/nYWIvWJ.png"
              alt="Nextcoders Logo"
              className="h-8"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders';
              }}
            />
          </Link>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-sm font-semibold text-gray-600">
              <Globe size={16} />
              {t('nextshot_privacy_page.language')}
            </div>
            <div className="flex items-center bg-gray-50 border border-gray-200 rounded-full overflow-hidden">
              <button
                type="button"
                onClick={() => i18n.changeLanguage('es')}
                className={`px-4 py-2 text-sm font-semibold transition ${
                  activeLang === 'es' ? 'bg-[#141414] text-white' : 'text-gray-700 hover:bg-white'
                }`}
              >
                ES
              </button>
              <button
                type="button"
                onClick={() => i18n.changeLanguage('en')}
                className={`px-4 py-2 text-sm font-semibold transition ${
                  activeLang === 'en' ? 'bg-[#141414] text-white' : 'text-gray-700 hover:bg-white'
                }`}
              >
                EN
              </button>
              <button
                type="button"
                onClick={() => i18n.changeLanguage('pt')}
                className={`px-4 py-2 text-sm font-semibold transition ${
                  activeLang === 'pt' ? 'bg-[#141414] text-white' : 'text-gray-700 hover:bg-white'
                }`}
              >
                PT
              </button>
            </div>
            <Link
              to="/nextshot"
              className="hidden md:inline-flex bg-white border border-gray-200 text-[#141414] hover:bg-gray-50 font-bold py-2 px-5 rounded-full transition"
            >
              NextShot
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-28 pb-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/nextshot'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('nextshot_privacy_page.back')}
          </button>

          <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6 border border-blue-100">
              <ShieldCheck size={16} />
              {t('nextshot_privacy_page.privacy')}
            </div>

            {!policyContent ? (
              <div className="text-gray-600 leading-relaxed">{t('nextshot_privacy_page.load_error')}</div>
            ) : (
              <div>{parsedContent}</div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default NextshotPrivacyPage;
